import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { NgxZendeskWebwidgetService } from '@nitsanzo/ngx-zendesk-webwidget';
import { AuthService } from '../../services/auth.service';
import { LoggerService } from '../../services/logger.service';
import { ConfigService } from '../../services/config.service';
import { Observable } from 'rxjs';
import { ReportComment, ReportRequest, Requester, RequestWrapper, ResourceIssue } from '../../models/report-request';
import { LocaleService } from '../../services/locale.service';
import { Util } from '../../util';
import { CookiesService } from '../../_core/state/dialogs/cookies/service/cookies.service';
import { UserFacade } from '../../_core/state/user/user.facade';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class ZendeskService {
  private readonly http = inject(HttpClient);
  private readonly logger = inject(LoggerService);
  private readonly ngxZendeskService = inject(NgxZendeskWebwidgetService);
  private readonly configService = inject(ConfigService);
  private readonly localeService = inject(LocaleService);
  private readonly authService = inject(AuthService);
  private readonly cookiesService = inject(CookiesService);
  private readonly userFacade = inject(UserFacade);

  init(locale?: string) {
    if (!this.configService.get().showZendesk) {
      return;
    }

    if (!this.cookiesService.hasConsentForCookie('zendesk')) {
      this.logger.warn('No Zendesk for you');
      return;
    }

    this.initSettingsZendesk();
    this.ngxZendeskService.initZendesk().then(() => {
      this.ngxZendeskService.zE('webWidget', 'setLocale', locale);

      if (this.authService.isAuthenticated()) {
        const user = this.userFacade.user;

        this.ngxZendeskService.zE('webWidget', 'identify', {
          name: user.name,
          email: user.email,
        });

        this.ngxZendeskService.zE('webWidget', 'prefill', {
          name: { value: user.name },
          email: { value: user.email },
        });
      }
    });
  }

  report(wrongMetadata: boolean, wrongCover: boolean, playingIssues: boolean, details: string): Observable<void> {
    const issue: ResourceIssue = {
      resourceUrl: window.location.href,
      wrongMetadata,
      wrongCover,
      playingIssues,
      details,
    };

    const request: ReportRequest = {
      requester: this.buildRequester(),
      comment: this.buildComment(issue),
      subject: '[OPAC] Report a problem',
      custom_fields: [
        {
          id: 23937516,
          value: 'LetminoTK',
        },
      ],
    };

    const wrapper: RequestWrapper = { request };
    return this.http.post<void>(`${environment.apiUrl}/zendesk/report`, wrapper);
  }

  private initSettingsZendesk() {
    window.zESettings = {
      webWidget: {
        launcher: {
          chatLabel: {
            ar: 'الدعم',
            ca: 'Suport',
            de: 'Die Unterstützung',
            en: 'Support',
            es: 'Soporte',
            eu: 'Laguntza',
            fr: 'Assistance',
            gl: 'Apoiar',
            he: 'תמיכה',
            nl: 'Steun',
            pt: 'Suporte',
            tr: 'Destek',
            uk: 'Apoiar',
            va: 'Suport',
            zh: '支持',
            '*': 'Support',
          },
        },
        contactOptions: {
          chatLabelOnline: {
            ar: 'الدعم',
            ca: 'Suport',
            de: 'Die Unterstützung',
            en: 'Support',
            es: 'Soporte',
            eu: 'Laguntza',
            fr: 'Assistance',
            gl: 'Apoiar',
            he: 'תמיכה',
            nl: 'Steun',
            pt: 'Suporte',
            tr: 'Destek',
            uk: 'Apoiar',
            va: 'Suport',
            zh: '支持',
            '*': 'Support',
          },
        },
        color: { theme: '#fff' },
        position: {
          horizontal: this.localeService.getTextDirection() === 'rtl' ? 'right' : 'left',
        },
      },
    };
  }

  private buildRequester(): Requester {
    // TODO: we're going to need this in the near future
    // if (this.authService.isAuthenticated()) {
    //   const user = this.authService.getLoggedUser();
    //
    //   return {
    //     name: user.name,
    //     email: user.email,
    //     locale: this.localeService.language
    //   };
    // }
    //
    // return {
    //   name: 'Anonymous',
    //   locale: this.localeService.language
    // };

    return {
      name: 'LETMINO Support',
      email: 'enterprise@odilo.us',
    };
  }

  private buildComment(issue: ResourceIssue): ReportComment {
    const cleanedIssue: ResourceIssue = Object.fromEntries(Object.entries(issue).filter(([, value]) => !!value));

    const body = Object.entries(cleanedIssue)
      .map(([k, v]) => `${Util.unCamelCase(k)}: ${v}`)
      .join('\n');

    return { body, created_at: new Date() };
  }
}
